import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { SOLANA_NETWORK } from "@/lib/solana-connection"

interface NetworkBadgeProps {
  size?: "sm" | "md" | "lg"
  showLabel?: boolean
  className?: string
}

/**
 * Network Badge Component
 * Displays the current Solana network (Devnet/Mainnet) with visual indicator
 * Only renders when not on mainnet-beta
 * Reads from VITE_SOLANA_NETWORK environment variable
 */
export function NetworkBadge({
  size = "md",
  showLabel = true,
  className,
}: NetworkBadgeProps) {
  const { t } = useTranslation()
  // Don't render on mainnet - production should be clean
  if (SOLANA_NETWORK === "mainnet-beta") {
    return null
  }

  // Format network name for display
  const networkLabel = SOLANA_NETWORK === "devnet" ? t("common.devnet") : SOLANA_NETWORK

  // Size configurations
  const sizeConfig = {
    sm: {
      dot: "w-1.5 h-1.5",
      text: "text-[10px]",
      padding: showLabel ? "px-1.5 py-0.5" : "p-1",
      gap: "gap-1",
      badge: false,
    },
    md: {
      dot: "w-1.5 h-1.5",
      text: "text-xs",
      padding: "px-2 py-1",
      gap: "gap-1.5",
      badge: true,
    },
    lg: {
      dot: "w-2 h-2",
      text: "text-sm",
      padding: "px-3 py-2",
      gap: "gap-2",
      badge: true,
    },
  }

  const config = sizeConfig[size]

  return (
    <div
      className={cn(
        "flex items-center shrink-0",
        config.gap,
        config.padding,
        config.badge && "rounded-full bg-amber-500/10 border border-amber-500/20",
        className
      )}
      title={t("common.connectedTo", { network: networkLabel })}
    >
      {/* Animated status dot */}
      <span className="relative flex h-2 w-2">
        <span
          className={cn(
            "animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75",
            size === "sm" && "hidden" // Hide ping on smallest size
          )}
        />
        <span
          className={cn(
            "relative inline-flex rounded-full bg-amber-500",
            config.dot
          )}
        />
      </span>

      {/* Network label */}
      {showLabel && (
        <span
          className={cn(
            "font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400 whitespace-nowrap",
            config.text
          )}
        >
          {networkLabel}
        </span>
      )}
    </div>
  )
}

/**
 * Simple dot-only indicator for compact spaces
 * Always visible regardless of network (useful for debugging)
 */
export function NetworkIndicator({
  className,
}: {
  className?: string
}) {
  const { t } = useTranslation()
  if (SOLANA_NETWORK === "mainnet-beta") {
    return null
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/10 border border-amber-500/20",
        className
      )}
      title={SOLANA_NETWORK === "devnet" ? t("common.devnet") : SOLANA_NETWORK}
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
        <span className="relative inline-flex rounded-full bg-amber-500 w-1.5 h-1.5" />
      </span>
    </div>
  )
}

export default NetworkBadge
